/**
 * 错误消息提取和映射
 * 将技术错误消息转换为用户友好的中文提示
 */

interface ErrorWithResponse {
  response?: {
    status?: number;
    data?: {
      message?: string;
      code?: number;
    };
  };
  message?: string;
}

/**
 * 提取友好的错误消息
 */
export function extractErrorMessage(error: unknown): string {
  if (!error) {
    return "发生了未知错误";
  }

  // 如果是字符串错误
  if (typeof error === "string") {
    return mapErrorMessage(error);
  }

  // 如果是 Error 对象
  if (error instanceof Error) {
    const err = error as Error & ErrorWithResponse;

    // 检查是否有响应数据
    if (err.response?.data?.message) {
      return mapErrorMessage(err.response.data.message);
    }

    // 检查状态码
    if (err.response?.status) {
      return mapStatusCodeToMessage(err.response.status);
    }

    // 使用错误消息
    if (err.message) {
      return mapErrorMessage(err.message);
    }
  }

  return "发生了未知错误";
}

/**
 * 将状态码映射为友好消息
 */
function mapStatusCodeToMessage(status: number): string {
  const statusMessages: Record<number, string> = {
    400: "请求参数错误，请检查输入",
    401: "未授权，请重新登录",
    403: "权限不足，无法访问此资源",
    404: "请求的资源不存在",
    409: "资源冲突，请检查后重试",
    422: "请求参数验证失败，请检查输入",
    429: "请求过于频繁，请稍后再试",
    500: "服务器内部错误，请稍后重试",
    502: "网关错误，请稍后重试",
    503: "服务暂时不可用，请稍后重试",
    504: "请求超时，请稍后重试",
  };

  return statusMessages[status] || `请求失败 (${status})`;
}

/**
 * 将技术错误消息映射为友好消息
 */
function mapErrorMessage(message: string): string {
  const errorMappings: Record<string, string> = {
    // 网络错误
    "Network request failed": "网络请求失败，请检查网络连接",
    "Failed to fetch": "网络请求失败，请检查网络连接",
    "NetworkError": "网络错误，请检查网络连接",
    "timeout": "请求超时，请稍后重试",

    // 认证错误
    "unauthorized": "未授权，请重新登录",
    "Unauthorized": "未授权，请重新登录",
    "invalid token": "登录已过期，请重新登录",
    "token expired": "登录已过期，请重新登录",
    "invalid credentials": "用户名或密码错误",
    "user not found": "用户不存在",
    "password incorrect": "密码错误",

    // 权限错误
    "forbidden": "权限不足",
    "Forbidden": "权限不足",
    "permission denied": "权限不足，无法执行此操作",
    "access denied": "访问被拒绝",

    // 验证错误
    "validation failed": "数据验证失败，请检查输入",
    "invalid input": "输入数据无效，请检查输入",
    "required field": "必填字段未填写",
    "invalid format": "数据格式不正确",

    // 资源错误
    "not found": "资源不存在",
    "Not Found": "资源不存在",
    "already exists": "资源已存在",
    "duplicate": "数据重复，请检查后重试",

    // 文件上传错误
    "file too large": "文件大小超过限制",
    "invalid file type": "不支持的文件类型",
    "upload failed": "文件上传失败",
    "image validation failed": "图片验证失败，请检查图片格式和大小",

    // 数据库错误
    "database error": "数据库错误，请稍后重试",
    "query failed": "查询失败，请稍后重试",
    "insert failed": "保存失败，请稍后重试",
    "update failed": "更新失败，请稍后重试",
    "delete failed": "删除失败，请稍后重试",

    // 验证码错误
    "invalid captcha": "验证码错误，请重新验证",
    "captcha expired": "验证码已过期，请刷新验证码",
    "captcha verification failed": "验证码验证失败",
    "captcha generation rate limit exceeded": "验证码生成频率过高，请稍后再试",
    "too many verification failures": "验证失败次数过多，请稍后再试",
    "captcha_not_found": "验证码不存在或已过期",
    "captcha_expired": "验证码已过期",
    "invalid_code": "验证码错误",
    "captcha generation rate limit exceeded, please wait": "验证码生成频率过高，请稍后再试",
    "too many verification failures, please wait": "验证失败次数过多，请稍后再试",
    "invalid captcha code": "验证码错误，请重新验证",
    "captcha code expired": "验证码已过期，请刷新验证码",
    "captcha not found": "验证码不存在",
    "captcha loading failed": "验证码加载失败，请重试",
    "browser environment check failed": "浏览器环境检测失败",
    "missing encryption API support": "缺少加密API支持",
    "POW verification failed": "POW验证失败，请重试",
    "POW captcha requires browser environment": "POW验证码需要浏览器环境",
    "POW captcha requires encryption API support": "POW验证码需要加密API支持",
    "captcha can only be used in browser environment": "验证码只能在浏览器环境中使用",
    "POW captcha requires solution": "POW验证码需要解决方案",
    "please select correct images": "请选择正确的图片",
    "security verification not completed": "安全验证未完成",
    "please complete captcha verification": "请完成验证码验证",

    // 通用错误
    "internal server error": "服务器内部错误，请稍后重试",
    "server error": "服务器错误，请稍后重试",
    "bad request": "请求参数错误",
    "Request failed": "请求失败",
    "Token expired and refresh failed": "登录已过期且刷新失败，请重新登录",
  };

  // 精确匹配
  if (errorMappings[message]) {
    return errorMappings[message];
  }

  // 模糊匹配（不区分大小写）
  const lowerMessage = message.toLowerCase();
  for (const [key, value] of Object.entries(errorMappings)) {
    if (lowerMessage.includes(key.toLowerCase())) {
      return value;
    }
  }

  // 如果消息是中文，直接返回
  if (/[\u4e00-\u9fa5]/.test(message)) {
    return message;
  }

  // 默认返回原始消息
  return message || "发生了未知错误";
}

/**
 * 获取错误类型（用于错误页面）
 */
export function getErrorType(error: unknown): {
  status: number;
  title: string;
  description: string;
} {
  if (error instanceof Error && "response" in error) {
    const err = error as Error & ErrorWithResponse;
    const status = err.response?.status || 500;

    const errorTypes: Record<number, { title: string; description: string }> = {
      400: {
        title: "请求错误",
        description: "您的请求格式不正确，请检查后重试",
      },
      401: {
        title: "未授权",
        description: "您需要登录才能访问此页面",
      },
      403: {
        title: "权限不足",
        description: "您没有权限访问此资源",
      },
      404: {
        title: "页面不存在",
        description: "抱歉，您访问的页面不存在",
      },
      409: {
        title: "资源冲突",
        description: "请求的资源发生冲突，请检查后重试",
      },
      429: {
        title: "请求过多",
        description: "请求过于频繁，请稍后再试",
      },
      500: {
        title: "服务器错误",
        description: "服务器遇到了问题，请稍后重试",
      },
      502: {
        title: "网关错误",
        description: "网关服务器出错，请稍后重试",
      },
      503: {
        title: "服务不可用",
        description: "服务暂时不可用，请稍后重试",
      },
      504: {
        title: "请求超时",
        description: "请求超时，请稍后重试",
      },
    };

    const errorType = errorTypes[status] || {
      title: "未知错误",
      description: "发生了未知错误",
    };

    return {
      status,
      ...errorType,
    };
  }

  return {
    status: 500,
    title: "未知错误",
    description: "发生了未知错误",
  };
}
